import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminShell from "@/components/AdminShell";
import api, { API } from "@/lib/api";
import { Download, Save, Bookmark, Trash2 } from "lucide-react";

const PLAN_OPTIONS = ["", "trial", "starter", "growth", "scale", "comp", "churned"];
const STATUS_OPTIONS = ["", "active", "locked", "deleted"];
const LAST_LOGIN_BUCKETS = [
  { value: "", label: "All" },
  { value: "active7d", label: "Active 7d" },
  { value: "dormant_7_30", label: "Dormant 7–30d" },
  { value: "dormant_30_plus", label: "Dormant 30d+" },
];

export default function AdminUsers() {
  const [q, setQ] = useState("");
  const [plan, setPlan] = useState("");
  const [status, setStatus] = useState("");
  const [bucket, setBucket] = useState("");
  const [hasComp, setHasComp] = useState("");
  const [internal, setInternal] = useState("");
  const [signupFrom, setSignupFrom] = useState("");
  const [signupTo, setSignupTo] = useState("");
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [segments, setSegments] = useState([]);
  const [segmentName, setSegmentName] = useState("");

  const filters = {
    q: q.trim() || undefined,
    plan: plan || undefined,
    status: status || undefined,
    last_login_bucket: bucket || undefined,
    has_comp: hasComp === "" ? undefined : hasComp === "yes",
    internal: internal === "" ? undefined : internal === "yes",
    signup_from: signupFrom || undefined,
    signup_to: signupTo || undefined,
  };

  const params = () =>
    Object.entries(filters)
      .filter(([, v]) => v !== undefined && v !== "")
      .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
      .join("&");

  useEffect(() => {
    document.title = "Admin · Users";
  }, []);

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get(`/admin/users?${params()}&limit=500`);
        setUsers(r.data.users || []);
        setTotal(r.data.total || 0);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, plan, status, bucket, hasComp, internal, signupFrom, signupTo]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/admin/segments");
        setSegments(r.data || []);
      } catch {
        /* ignore */
      }
    })();
  }, []);

  const sorted = [...users].sort((a, b) => {
    const av = a[sortBy] ?? "";
    const bv = b[sortBy] ?? "";
    if (av === bv) return 0;
    return (sortDir === "asc" ? 1 : -1) * (av > bv ? 1 : -1);
  });

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortDir("desc");
    }
  };

  const exportCsv = () => {
    const url = `${API}/admin/users/export.csv?${params()}`;
    window.open(url, "_blank");
  };

  const saveSegment = async () => {
    if (!segmentName.trim()) return;
    const r = await api.post("/admin/segments", {
      name: segmentName.trim(),
      filters_json: filters,
    });
    setSegments((prev) => [r.data, ...prev]);
    setSegmentName("");
  };

  const loadSegment = (s) => {
    const f = s.filters_json || {};
    setQ(f.q || "");
    setPlan(f.plan || "");
    setStatus(f.status || "");
    setBucket(f.last_login_bucket || "");
    setHasComp(
      f.has_comp === true ? "yes" : f.has_comp === false ? "no" : ""
    );
    setInternal(
      f.internal === true ? "yes" : f.internal === false ? "no" : ""
    );
    setSignupFrom(f.signup_from || "");
    setSignupTo(f.signup_to || "");
  };

  const deleteSegment = async (id) => {
    await api.delete(`/admin/segments/${id}`);
    setSegments((prev) => prev.filter((s) => s.segment_id !== id));
  };

  return (
    <AdminShell>
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
            Users
          </div>
          <h1 className="display mt-1 text-3xl font-black tracking-tight">
            {total.toLocaleString()} total
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={exportCsv}
            data-testid="users-export-csv"
            className="inline-flex items-center gap-1.5 bg-zinc-900 border border-zinc-700 text-xs px-3 py-1.5 rounded-[3px] hover:bg-zinc-800"
          >
            <Download size={12} /> Export CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-zinc-900/60 border border-zinc-800 rounded-[4px] p-3 grid grid-cols-2 md:grid-cols-6 gap-2" data-testid="users-filters">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search email / name / brand"
          data-testid="users-q"
          className="col-span-2 bg-zinc-950 border border-zinc-800 rounded-[3px] px-2 py-1.5 text-xs focus:outline-none focus:border-zinc-600"
        />
        <Select value={plan} onChange={setPlan} testid="filter-plan">
          <option value="">All plans</option>
          {PLAN_OPTIONS.slice(1).map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </Select>
        <Select value={status} onChange={setStatus} testid="filter-status">
          <option value="">All statuses</option>
          {STATUS_OPTIONS.slice(1).map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </Select>
        <Select value={bucket} onChange={setBucket} testid="filter-login-bucket">
          {LAST_LOGIN_BUCKETS.map((b) => (
            <option key={b.value} value={b.value}>
              {b.label}
            </option>
          ))}
        </Select>
        <Select value={hasComp} onChange={setHasComp} testid="filter-has-comp">
          <option value="">Comp: any</option>
          <option value="yes">With active comp</option>
          <option value="no">No active comp</option>
        </Select>
        <Select value={internal} onChange={setInternal} testid="filter-internal">
          <option value="">Internal: any</option>
          <option value="yes">Internal only</option>
          <option value="no">External only</option>
        </Select>
        <input
          type="date"
          value={signupFrom}
          onChange={(e) => setSignupFrom(e.target.value)}
          data-testid="filter-signup-from"
          className="bg-zinc-950 border border-zinc-800 rounded-[3px] px-2 py-1.5 text-xs text-zinc-300 focus:outline-none focus:border-zinc-600"
        />
        <input
          type="date"
          value={signupTo}
          onChange={(e) => setSignupTo(e.target.value)}
          data-testid="filter-signup-to"
          className="bg-zinc-950 border border-zinc-800 rounded-[3px] px-2 py-1.5 text-xs text-zinc-300 focus:outline-none focus:border-zinc-600"
        />
      </div>

      {/* Saved segments */}
      <div className="mt-3 bg-zinc-900/40 border border-zinc-800 rounded-[4px] p-3" data-testid="saved-segments">
        <div className="flex items-center gap-2 flex-wrap">
          <Bookmark size={12} className="text-zinc-500" />
          <span className="mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">Saved</span>
          {segments.map((s) => (
            <div key={s.segment_id} className="inline-flex items-center gap-1 text-xs bg-zinc-900 border border-zinc-800 rounded-[3px]">
              <button
                onClick={() => loadSegment(s)}
                data-testid={`segment-${s.segment_id}`}
                className="px-2 py-1 hover:bg-zinc-800"
              >
                {s.name}
              </button>
              <button
                onClick={() => deleteSegment(s.segment_id)}
                className="px-1.5 py-1 hover:text-rose-400"
                title="Delete segment"
              >
                <Trash2 size={10} />
              </button>
            </div>
          ))}
          <div className="ml-auto flex items-center gap-1">
            <input
              value={segmentName}
              onChange={(e) => setSegmentName(e.target.value)}
              placeholder="Name this segment…"
              data-testid="segment-name-input"
              className="bg-zinc-950 border border-zinc-800 rounded-[3px] px-2 py-1 text-xs"
            />
            <button
              onClick={saveSegment}
              disabled={!segmentName.trim()}
              data-testid="save-segment-btn"
              className="inline-flex items-center gap-1 bg-zinc-900 border border-zinc-700 text-xs px-2 py-1 rounded-[3px] hover:bg-zinc-800 disabled:opacity-40"
            >
              <Save size={11} /> Save
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="mt-4 bg-zinc-900/60 border border-zinc-800 rounded-[4px] overflow-hidden">
        <div className="overflow-auto">
          <table className="w-full text-xs" data-testid="users-table">
            <thead className="bg-zinc-900 sticky top-0">
              <tr className="mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                <ColHead field="email" label="Email" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
                <ColHead field="name" label="Name" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
                <ColHead field="created_at" label="Signup" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
                <ColHead field="plan" label="Plan" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
                <th className="text-right p-2">MRR £</th>
                <ColHead field="last_login_at" label="Last login" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
                <th className="text-right p-2">Projects</th>
                <th className="text-left p-2">Status</th>
                <th className="text-center p-2">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {loading && (
                <tr>
                  <td colSpan={9} className="p-4 text-center text-zinc-500">
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && sorted.length === 0 && (
                <tr>
                  <td colSpan={9} className="p-4 text-center text-zinc-500">
                    No users match.
                  </td>
                </tr>
              )}
              {sorted.map((u) => (
                <tr
                  key={u.user_id}
                  className="hover:bg-zinc-900/70"
                  data-testid={`user-row-${u.user_id}`}
                >
                  <td className="p-2">
                    <Link to={`/admin/users/${u.user_id}`} className="hover:underline">
                      {u.email}
                    </Link>
                    {u.internal_account && (
                      <span className="ml-2 text-[9px] px-1 bg-zinc-800 rounded-sm">
                        internal
                      </span>
                    )}
                  </td>
                  <td className="p-2 text-zinc-400">{u.name || "—"}</td>
                  <td className="p-2 mono text-zinc-500 text-[10px]">
                    {u.created_at?.slice(0, 10)}
                  </td>
                  <td className="p-2">
                    <PlanBadge plan={u.plan} isComp={u.has_active_comp} />
                  </td>
                  <td className="p-2 text-right mono tabular-nums">
                    £{u.mrr_contribution_gbp}
                  </td>
                  <td className="p-2 mono text-zinc-500 text-[10px]">
                    {u.last_login_at?.slice(0, 10) || "—"}
                  </td>
                  <td className="p-2 text-right mono tabular-nums">
                    {u.project_count}
                  </td>
                  <td className="p-2">
                    <StatusBadge status={u.account_status || "active"} />
                  </td>
                  <td className="p-2 text-center">
                    <Link
                      to={`/admin/users/${u.user_id}`}
                      className="text-[10px] mono uppercase tracking-[0.16em] text-zinc-400 hover:text-white"
                    >
                      Open →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AdminShell>
  );
}

function ColHead({ field, label, sortBy, sortDir, onClick }) {
  const active = sortBy === field;
  return (
    <th
      onClick={() => onClick(field)}
      className="text-left p-2 cursor-pointer select-none hover:text-white"
    >
      {label}
      {active && <span className="ml-1">{sortDir === "asc" ? "↑" : "↓"}</span>}
    </th>
  );
}

function Select({ value, onChange, testid, children }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="bg-zinc-950 border border-zinc-800 rounded-[3px] px-2 py-1.5 text-xs text-zinc-300 focus:outline-none focus:border-zinc-600"
    >
      {children}
    </select>
  );
}

function PlanBadge({ plan, isComp }) {
  const cls =
    {
      trial: "bg-zinc-800 text-zinc-300",
      starter: "bg-emerald-900/40 text-emerald-300 border border-emerald-700",
      growth: "bg-sky-900/40 text-sky-300 border border-sky-700",
      scale: "bg-amber-900/40 text-amber-300 border border-amber-700",
      comp: "bg-purple-900/40 text-purple-300 border border-purple-700",
      churned: "bg-rose-900/40 text-rose-300 border border-rose-700",
    }[plan] || "bg-zinc-800 text-zinc-300";
  return (
    <span
      className={`inline-flex items-center text-[10px] mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-sm ${cls}`}
    >
      {plan}
      {isComp && <span className="ml-1 text-amber-400">★</span>}
    </span>
  );
}

function StatusBadge({ status }) {
  const cls =
    status === "active"
      ? "text-emerald-300"
      : status === "locked"
      ? "text-amber-300"
      : "text-rose-300";
  return (
    <span className={`text-[10px] mono uppercase tracking-[0.14em] ${cls}`}>
      {status}
    </span>
  );
}
